import PublicHeader from '../components/PublicHeader';
import SiteFooter from '../components/SiteFooter';
import { Link } from 'react-router-dom';

export default function TermsOfServicePage() {
  return (
    <div className="min-h-screen bg-dark-900 flex flex-col">
      <PublicHeader />

      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-3xl font-bold text-white mb-1"><span role="img" aria-label="scales">⚖️</span> Terms of Service</h1>
          <p className="text-sm text-gray-500 mb-10">Effective: March 24, 2026</p>

          <div className="space-y-8 text-gray-400 leading-relaxed">

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
              <p>You must be at least 13 years of age (or 16 in the European Union) to use the Service. By using the Service, you represent that you meet this requirement and have the legal capacity to enter into this agreement. If you are using the Service on behalf of an organisation, you represent that you have authority to bind that organisation to these Terms.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">2. Description of the Service</h2>
              <p className="mb-3">Clipto is a Twitch clip management tool that allows streamers to:</p>
              <ul className="list-disc list-inside space-y-1 pl-2 mb-3">
                <li>Import and organise Twitch clip metadata from Twitch channels</li>
                <li>Build collections and apply filters, tags, and sorting</li>
                <li>Generate and download HTML export pages for clip review sessions</li>
                <li>Manage subscriptions for premium features</li>
              </ul>
              <p>The Service requires a valid Twitch account for authentication. We do not host or store Twitch video content — we only store metadata (titles, URLs, thumbnails, etc.) retrieved from the Twitch API.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">3. Account Registration</h2>

              <h3 className="font-semibold text-gray-200 mb-2">3.1 Twitch Authentication</h3>
              <p className="mb-4">Access to the Service requires logging in with a Twitch account via OAuth. By doing so, you grant Clipto permission to read your Twitch user profile and email address. You remain responsible for maintaining the security of your Twitch account.</p>

              <h3 className="font-semibold text-gray-200 mb-2">3.2 Account Responsibility</h3>
              <p className="mb-4">You are responsible for all activity that occurs under your account. If you suspect any unauthorised use of your account, <Link to="/contact" className="text-primary hover:underline">contact us</Link> immediately.</p>

              <h3 className="font-semibold text-gray-200 mb-2">3.3 One Account per User</h3>
              <p>You may only maintain one account unless we explicitly permit otherwise. Creating multiple accounts to circumvent restrictions or abuse free tier limits is prohibited.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">4. Subscriptions and Payments</h2>

              <h3 className="font-semibold text-gray-200 mb-2">4.1 Free and Paid Tiers</h3>
              <p className="mb-4">Clipto offers a free tier with limited features and paid subscription tiers that unlock additional capabilities. The features at each tier are described on the <Link to="/pricing" className="text-primary hover:underline">Pricing page</Link>.</p>

              <h3 className="font-semibold text-gray-200 mb-2">4.2 Billing</h3>
              <p className="mb-4">Paid plans are billed on a recurring basis (monthly or annually) via Stripe. By subscribing, you authorise Stripe to charge your payment method on the applicable billing cycle. All prices are in USD and exclusive of applicable taxes unless stated otherwise.</p>

              <h3 className="font-semibold text-gray-200 mb-2">4.3 Cancellation</h3>
              <p className="mb-4">You may cancel your subscription at any time from your account settings. Cancellation takes effect at the end of the current billing period. You retain access to paid features until the end of that period.</p>

              <h3 className="font-semibold text-gray-200 mb-2">4.4 Refunds</h3>
              <p className="mb-4">Subscription fees are generally non-refundable. If you believe you were charged in error, <Link to="/contact" className="text-primary hover:underline">contact us</Link> within 14 days of the charge and we will review your request.</p>

              <h3 className="font-semibold text-gray-200 mb-2">4.5 Price Changes</h3>
              <p>We reserve the right to change our pricing at any time. If you are on an active subscription, we will notify you at least 30 days in advance before any price increase takes effect.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">5. Acceptable Use</h2>
              <p className="mb-3">You agree not to use the Service to:</p>
              <ul className="list-disc list-inside space-y-1 pl-2 mb-3">
                <li>Violate any applicable law or regulation</li>
                <li>Infringe the intellectual property rights of any third party</li>
                <li>Circumvent access controls, rate limits, or security measures</li>
                <li>Access the Service by automated means without our written permission</li>
                <li>Attempt to gain unauthorised access to other user accounts or our systems</li>
                <li>Distribute malware or engage in phishing or fraud</li>
                <li>Harass, abuse, or harm any person</li>
              </ul>
              <p>Violation of these rules may result in immediate suspension or termination of your account.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">6. Twitch Content and Intellectual Property</h2>

              <h3 className="font-semibold text-gray-200 mb-2">6.1 Twitch Clip Ownership</h3>
              <p className="mb-4">Clips imported into Clipto originate from the Twitch platform and are subject to the Twitch Terms of Service and applicable copyright law. Clipto does not claim ownership over any Twitch content.</p>

              <h3 className="font-semibold text-gray-200 mb-2">6.2 Your Content</h3>
              <p className="mb-4">You retain ownership of any content you create within the Service (e.g., collection names, tags, exported HTML pages). By using the Service, you grant Clipto a limited, non-exclusive licence to store and process that content solely to provide the Service to you.</p>

              <h3 className="font-semibold text-gray-200 mb-2">6.3 Clipto Intellectual Property</h3>
              <p>All other content, software, design, trademarks, and branding on the Service are the exclusive property of Clipto. You may not copy, reproduce, or distribute them without our prior written consent.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">7. Exports and Generated Files</h2>
              <p>Export files (HTML/ZIP) generated by the Service are made available for download for a limited time (typically 1 hour). After this period, files may be deleted from our servers. You are responsible for downloading your exports promptly. Clipto is not responsible for how you use or distribute exported files or for any content within the clips you include.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">8. Waiting List</h2>
              <p>If you join the waiting list, we will contact you at the email address you provide when access becomes available. Joining the waiting list does not guarantee access or any specific timeline.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">9. Service Availability and Modifications</h2>
              <p>We do not guarantee uninterrupted or error-free access. We may perform maintenance, modify or discontinue features, or throttle usage to maintain service quality. We are not liable for any loss or inconvenience arising from downtime.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">10. Termination</h2>

              <h3 className="font-semibold text-gray-200 mb-2">10.1 By You</h3>
              <p className="mb-4">You may delete your account at any time from your account settings. Your personal data will be removed as described in our <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>.</p>

              <h3 className="font-semibold text-gray-200 mb-2">10.2 By Clipto</h3>
              <p>We may suspend or terminate your account if you violate these Terms, engage in fraudulent or abusive activity, or if we are required to do so by law. No refunds will be issued for termination for cause.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">11. Disclaimer of Warranties</h2>
              <p>The Service is provided <strong className="text-gray-300">"as is"</strong> and <strong className="text-gray-300">"as available"</strong> without warranty of any kind. We disclaim all implied warranties, including merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the Service will be uninterrupted, accurate, or error-free.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">12. Limitation of Liability</h2>
              <p>To the fullest extent permitted by applicable law, Clipto is not liable for any indirect, incidental, special, consequential, or punitive damages. Our total liability for any claim shall not exceed the amount you paid to Clipto in the 12 months preceding the claim.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">13. Indemnification</h2>
              <p>You agree to indemnify and hold harmless Clipto and its operators from any claims, liabilities, and expenses arising from your use of the Service, your violation of these Terms, or content you create, import, or export through the Service.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">14. Governing Law and Disputes</h2>
              <p>These Terms are governed by applicable law. We encourage resolving disputes through good-faith negotiation first. Any unresolved disputes shall be referred to the courts of competent jurisdiction. If any provision of these Terms is found unenforceable, the remaining provisions continue in full effect.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">15. Changes to These Terms</h2>
              <p>We may update these Terms at any time. Material changes will be communicated via your account email or a prominent notice on the Service at least 14 days before taking effect. Continued use after that date constitutes acceptance of the revised Terms.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-white mb-4">16. Contact</h2>
              <p>For questions about these Terms, <Link to="/contact" className="text-primary hover:underline">visit our Contact page</Link>.</p>
            </section>

          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
